import { createHash } from "react-native-quick-crypto";
import { createSalt, hashPasscode } from "./screenTimePasscode.js";

const HELP_ESCROW_PEPPER = "procsystim-help-escrow-v1";

/**
 * @param {string} material
 * @returns {Promise<Uint8Array>}
 */
async function keyBytesFromMaterial(material) {
  const hex = await createHash("sha256").update(material).digest("hex");
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function hexToBytes(hex) {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * @param {string} plain
 * @param {Uint8Array} keyBytes
 */
function xorCipher(plain, keyBytes) {
  const data = new TextEncoder().encode(plain);
  const out = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    out[i] = data[i] ^ keyBytes[i % keyBytes.length];
  }
  return bytesToHex(out);
}

/**
 * @param {string} encHex
 * @param {Uint8Array} keyBytes
 */
function xorDecipher(encHex, keyBytes) {
  const data = hexToBytes(encHex);
  const out = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    out[i] = data[i] ^ keyBytes[i % keyBytes.length];
  }
  return new TextDecoder().decode(out);
}

/**
 * @param {string} pin
 * @param {string} recoveryPassword
 * @param {string} salt
 */
export async function encryptPasscodeWithRecovery(pin, recoveryPassword, salt) {
  const key = await keyBytesFromMaterial(`recovery:${salt}:${recoveryPassword}`);
  return xorCipher(pin, key);
}

/**
 * @param {string} enc
 * @param {string} recoveryPassword
 * @param {string} salt
 */
export async function decryptPasscodeWithRecovery(enc, recoveryPassword, salt) {
  if (!enc || !recoveryPassword || !salt) return null;
  try {
    const key = await keyBytesFromMaterial(`recovery:${salt}:${recoveryPassword}`);
    return xorDecipher(enc, key);
  } catch {
    return null;
  }
}

/**
 * @param {string} pin
 * @param {string} escrowSalt
 */
export async function encryptPasscodeForHelpEscrow(pin, escrowSalt) {
  const key = await keyBytesFromMaterial(`${HELP_ESCROW_PEPPER}:${escrowSalt}`);
  return xorCipher(pin, key);
}

/**
 * @param {string} enc
 * @param {string} escrowSalt
 */
export async function decryptPasscodeFromHelpEscrow(enc, escrowSalt) {
  if (!enc || !escrowSalt) return null;
  try {
    const key = await keyBytesFromMaterial(`${HELP_ESCROW_PEPPER}:${escrowSalt}`);
    return xorDecipher(enc, key);
  } catch {
    return null;
  }
}

/**
 * @param {string} recoveryPassword
 * @param {string} salt
 */
export async function hashRecoveryPassword(recoveryPassword, salt) {
  return hashPasscode(recoveryPassword, salt);
}

/**
 * @param {string} recoveryPassword
 * @param {string} salt
 * @param {string} storedHash
 */
export async function verifyRecoveryPassword(recoveryPassword, salt, storedHash) {
  if (!recoveryPassword || !salt || !storedHash) return false;
  const h = await hashRecoveryPassword(recoveryPassword, salt);
  return h === storedHash;
}

export { createSalt };

/**
 * @param {string} email
 * @param {string} storedEmail
 */
export function emailsMatch(email, storedEmail) {
  return email.trim().toLowerCase() === storedEmail.trim().toLowerCase();
}
